/**
 * Snow Overlay Effect
 * Canvas-based snowfall animation that renders only during Christmas event
 * Respects prefers-reduced-motion accessibility setting
 */
import { useEffect, useMemo, useRef } from 'react'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import {
  OVERLAY_MAX_DPR,
  OVERLAY_TARGET_FPS,
  createAdaptiveQuality,
  createFrameGate,
} from './overlayPerf'

// Kill-switch for the snowfall animation. Perf is guarded by overlayPerf (DPR
// cap, 30fps throttle, adaptive quality that stops the overlay on low-spec
// devices); flip to false to hard-disable while keeping the static christmas
// theme CSS.
const SNOW_ANIMATION_ENABLED: boolean = true

type Flake = {
  x: number
  y: number
  r: number
  vx: number
  vy: number
  alpha: number
}

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n))

const createFlakes = (count: number, w: number, h: number): Flake[] => {
  const flakes: Flake[] = []
  for (let i = 0; i < count; i += 1) {
    flakes.push({
      x: Math.random() * w,
      y: Math.random() * h,
      r: 0.8 + Math.random() * 2.2,
      vx: -0.25 + Math.random() * 0.5, // Slightly less horizontal drift
      vy: 0.4 + Math.random() * 0.8, // Slower falling speed
      alpha: 0.25 + Math.random() * 0.6,
    })
  }
  return flakes
}

const SnowOverlay = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const flakesRef = useRef<Flake[]>([])

  const event = useCurrentEvent()
  const isVisibleRef = useRef(true)

  const shouldRender = useMemo(() => {
    if (!SNOW_ANIMATION_ENABLED) return false
    if (event !== EventType.CHRISTMAS) return false
    if (typeof window === 'undefined') return false

    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches
    return !reduceMotion
  }, [event])

  // Page visibility detection — uses ref to avoid re-running the animation effect
  useEffect(() => {
    if (!shouldRender) return
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [shouldRender])

  useEffect(() => {
    if (!shouldRender) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = clamp(window.devicePixelRatio || 1, 1, OVERLAY_MAX_DPR)
    let quality = createAdaptiveQuality(0, 10)

    const resize = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const flakeCount = clamp(Math.floor((w * h) / 50000), 25, 80) // Fewer flakes
      flakesRef.current = createFlakes(flakeCount, w, h)
      quality = createAdaptiveQuality(flakeCount, 10)
    }

    resize()
    canvas.style.display = ''
    let resizeTimer: number | undefined
    const onResize = () => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(resize, 150)
    }
    window.addEventListener('resize', onResize)

    const gate = createFrameGate(1000 / OVERLAY_TARGET_FPS)
    let last = performance.now()

    const tick = (now: number) => {
      rafRef.current = requestAnimationFrame(tick)

      // Pause animation when tab is hidden
      if (!isVisibleRef.current) {
        last = now
        return
      }

      const interval = now - last
      if (!gate(now)) return

      const canvasW = window.innerWidth
      const canvasH = window.innerHeight

      const dt = clamp(interval / 16.67, 0.5, 3)
      last = now
      const workStart = performance.now()

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, canvasW, canvasH)
      ctx.fillStyle = 'rgba(255, 255, 255, 1)'

      const particles = flakesRef.current
      for (let i = 0; i < particles.length; i++) {
        const f = particles[i]
        f.x += f.vx * dt
        f.y += f.vy * dt

        if (f.x < -10) f.x = canvasW + 10
        if (f.x > canvasW + 10) f.x = -10

        if (f.y > canvasH + 10) {
          f.y = -10
          f.x = Math.random() * canvasW
        }

        ctx.globalAlpha = f.alpha
        ctx.beginPath()
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2)
        ctx.fill()
      }

      ctx.globalAlpha = 1
      quality.record(performance.now() - workStart, interval)

      if (quality.stopped) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        canvas.style.display = 'none'
        return
      }

      const target = quality.count
      while (particles.length > target) particles.pop()
      if (particles.length < target)
        particles.push(
          ...createFlakes(target - particles.length, canvasW, canvasH),
        )
    }

    rafRef.current = requestAnimationFrame((now) => {
      last = now
      tick(now)
    })

    return () => {
      window.clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [shouldRender])

  if (!shouldRender) return null

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 9999 }}
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  )
}

export default SnowOverlay
